import { ComponentProps, useState } from "react";
import { useInView } from "react-intersection-observer";
import Card from "@particles/card/Card";
import { SanitizedFeedSectionArticle } from "@typings/Cue";
import axios from "axios";

type UseLoadMoreArticlesByProfileIdProps = {
  /** The initial list of articles to display. */
  initialList: ComponentProps<typeof Card>[];
  /** The ids of the profiles to fetch articles for. */
  profileIds: string[];
  /** The number of articles to display per page. */
  pageSize: number;
  /** The template props to use for the articles. */
  templateProps: (
    cardProps: SanitizedFeedSectionArticle[]
  ) => ComponentProps<typeof Card>[];
};

export const useLoadMoreArticlesByProfileId = ({
  initialList,
  profileIds,
  pageSize,
  templateProps,
}: UseLoadMoreArticlesByProfileIdProps) => {
  const [hasMoreStories, setHasMoreStories] = useState<boolean>(true);
  const [loadMoreData, setLoadMoreData] = useState<
    ComponentProps<typeof Card>[]
  >([]);
  const [clickableHandleLoadMore, setClickableHandleLoadMore] =
    useState<boolean>(true);

  const handleLoadMore = async (data: ComponentProps<typeof Card>[]) => {
    setClickableHandleLoadMore(false);
    const page = [...data, ...loadMoreData].length / pageSize + 1;

    const response = await axios.post<SanitizedFeedSectionArticle[]>(
      "/_plat/api/v1/more-stories-by-profile-id",
      { profileIds: profileIds, size: 10, page }
    );

    const moreArticles = response.data || [];
    const articleProps = templateProps(moreArticles);

    setLoadMoreData([...loadMoreData, ...articleProps]);

    if (moreArticles.length < pageSize) {
      setHasMoreStories(false);
    }

    setClickableHandleLoadMore(true);
  };

  const { ref } = useInView({
    threshold: 0,
    onChange: (inView) => {
      if (!inView) return;
      if (!hasMoreStories) return;
      if (initialList.length < pageSize) return;

      handleLoadMore(initialList);
    },
  });

  return {
    ref,
    handleLoadMore,
    hasMoreStories,
    loadMoreData,
    clickableHandleLoadMore,
  };
};
